import React from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from '../../../../hooks/useTranslation';
import { type SubscriptionPlan } from '../../../../helpers/orgSubscriptionHelper';
import {
    TRY_IT_FREE_FOR_14_DAYS,
    NO_PAYMENT_REQUIRED,
    NO_PAYMENT_REQUIRED_NOW,
    START_IMMEDIATELY_ZERO_COMMITMENT,
    FULL_FEATURE_ACCESS,
    EXPLORE_EVERYTHING,
    CANCEL_ANYTIME,
    NO_CHARGES_IF_CANCEL,
    BILLING_SUMMARY,
    AFTER_TRIAL_PERIOD,
    ESTIMATED_TAX,
    START_14_DAY_FREE_TRIAL,
    MONTHLY,
    YEARLY,
    MOST_POPULAR,
    SUBSCRIPTION_BEGINS_AUTOMATICALLY,
    FIRST_CHARGE_ON,
    THEN_RENEWS_MONTHLY_ON,
    THEN_RENEWS_YEARLY_ON,
    YOU_WONT_BE_CHARGED,
    START_SUBSCRIPTION_NOW,
    ACTIVE_PLAN,
    ADD_PAYMENT_METHOD_LINK,
} from '../../../../constants/strings';

const languageTexts = {
    TRY_IT_FREE_FOR_14_DAYS,
    NO_PAYMENT_REQUIRED,
    NO_PAYMENT_REQUIRED_NOW,
    START_IMMEDIATELY_ZERO_COMMITMENT,
    FULL_FEATURE_ACCESS,
    EXPLORE_EVERYTHING,
    CANCEL_ANYTIME,
    NO_CHARGES_IF_CANCEL,
    BILLING_SUMMARY,
    AFTER_TRIAL_PERIOD,
    ESTIMATED_TAX,
    START_14_DAY_FREE_TRIAL,
    MONTHLY,
    YEARLY,
    MOST_POPULAR,
    SUBSCRIPTION_BEGINS_AUTOMATICALLY,
    FIRST_CHARGE_ON,
    THEN_RENEWS_MONTHLY_ON,
    THEN_RENEWS_YEARLY_ON,
    YOU_WONT_BE_CHARGED,
    START_SUBSCRIPTION_NOW,
    ACTIVE_PLAN,
    ADD_PAYMENT_METHOD_LINK,
};

interface PlanConfirmationModalProps {
    selectedPlan: SubscriptionPlan | null;
    billingCycle: 'monthly' | 'yearly';
    onStartTrial: () => void;
    onStartSubscription: () => void;
    hasSubscription: boolean;
    isTrialEligible: boolean;
    activePriceId: string | null;
    isSubmitting: boolean;
    actionErrorMessage: string;
    onAddPaymentMethodClick: () => void;
}

const PlanConfirmationModal: React.FC<PlanConfirmationModalProps> = ({
    selectedPlan,
    billingCycle,
    onStartTrial,
    onStartSubscription,
    hasSubscription,
    isTrialEligible,
    activePriceId,
    isSubmitting,
    actionErrorMessage,
    onAddPaymentMethodClick,
}) => {
    const translatedText = useTranslation(languageTexts, 'none');

    if (!selectedPlan) return null;

    // Determine if trial should be shown: only if no subscription AND trial eligible
    const shouldShowTrial = !hasSubscription && isTrialEligible;

    const displayPrice = billingCycle === 'yearly' ? selectedPlan.yearlyPrice || 0 : selectedPlan.monthlyPrice || selectedPlan.price || 0;
    const periodText = billingCycle === 'yearly' ? translatedText.YEARLY : translatedText.MONTHLY;
    const totalAmount = displayPrice;

    // Calculate dates dynamically based on current system date
    const today = new Date(); // Current system date

    // Trial period: 14 days starting from today (today = day 1)
    // Day 1: today
    // Day 14: today + 13 days (last day of trial)
    // Subscription begins & First charge: today + 14 days (day after trial ends)
    const firstChargeDate = new Date(today);
    firstChargeDate.setDate(today.getDate() + 14); // Subscription begins and first charge date

    // Calculate next renewal date after first charge
    const nextRenewalDate = new Date(firstChargeDate);
    if (billingCycle === 'yearly') {
        nextRenewalDate.setFullYear(firstChargeDate.getFullYear() + 1);
    } else {
        nextRenewalDate.setMonth(firstChargeDate.getMonth() + 1);
    }

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };

    const firstChargeDateFormatted = formatDate(firstChargeDate); // First charge & subscription begins date
    const nextRenewalDateFormatted = formatDate(nextRenewalDate);

    return (
        <div className="plan-confirmation-modal">
            {/* Plan Header */}
            <div className="modal-plan-header">
                <div className="plan-header-content">
                    <div className="plan-title-row">
                        <h3>{selectedPlan.name}</h3>
                        {selectedPlan.isMostPopular && <span className="popular-badge-small">{translatedText.MOST_POPULAR}</span>}
                    </div>
                    <p className="plan-description-modal">{selectedPlan.description}</p>
                </div>
                <div className="plan-price-display">
                    <div className="price-wrapper">
                        <span className="price-large">${displayPrice}</span>
                        <span className="price-period-badge">{periodText}</span>
                    </div>
                </div>
            </div>

            {/* Free Trial Section */}
            {shouldShowTrial && (
                <div className="free-trial-section">
                    <div className="trial-header">
                        <div className="trial-icon">
                            <i className="ri-gift-line" />
                        </div>
                        <div className="trial-content">
                            <h4>{translatedText.TRY_IT_FREE_FOR_14_DAYS}</h4>
                            <p>{translatedText.NO_PAYMENT_REQUIRED_NOW?.replace('{0}', selectedPlan.name)}</p>
                            <p className="trial-start-date">
                                {translatedText.SUBSCRIPTION_BEGINS_AUTOMATICALLY?.replace('{0}', firstChargeDateFormatted)}
                            </p>
                        </div>
                    </div>

                    <div className="trial-benefits">
                        <div className="benefit-item">
                            <i className="ri-checkbox-circle-fill" />
                            <div>
                                <strong>{translatedText.NO_PAYMENT_REQUIRED}</strong>
                                <p>{translatedText.START_IMMEDIATELY_ZERO_COMMITMENT}</p>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <i className="ri-checkbox-circle-fill" />
                            <div>
                                <strong>{translatedText.FULL_FEATURE_ACCESS}</strong>
                                <p>{translatedText.EXPLORE_EVERYTHING?.replace('{0}', selectedPlan.name)}</p>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <i className="ri-checkbox-circle-fill" />
                            <div>
                                <strong>{translatedText.CANCEL_ANYTIME}</strong>
                                <p>{translatedText.NO_CHARGES_IF_CANCEL}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Billing Summary Section */}
            <div className="billing-summary-section">
                <div className="summary-header">
                    <i className="ri-file-list-line" />
                    <h4>
                        {translatedText.BILLING_SUMMARY}{' '}
                        {shouldShowTrial && <span className="after-trial-text">{translatedText.AFTER_TRIAL_PERIOD}</span>}
                    </h4>
                </div>

                <div className="summary-items">
                    <div className="summary-row">
                        <span>
                            {selectedPlan.name} ({periodText?.toLowerCase() || billingCycle})
                        </span>
                        <span>${displayPrice}</span>
                    </div>
                    <div className="summary-divider" />
                    <div className="summary-row summary-total">
                        <span>{translatedText.FIRST_CHARGE_ON?.replace('{0}', firstChargeDateFormatted)}</span>
                        <span className="total-amount">${totalAmount}</span>
                    </div>
                    <div className="summary-renewal-info">
                        {billingCycle === 'yearly'
                            ? translatedText.THEN_RENEWS_YEARLY_ON?.replace('{0}', nextRenewalDateFormatted)
                            : translatedText.THEN_RENEWS_MONTHLY_ON?.replace('{0}', nextRenewalDateFormatted)}
                    </div>
                </div>

                {shouldShowTrial && (
                    <div className="disclaimer">
                        <i className="ri-information-line" />
                        <p>{translatedText.YOU_WONT_BE_CHARGED?.replace('{0}', firstChargeDateFormatted)}</p>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="modal-actions">
                <Button
                    label={shouldShowTrial ? translatedText.START_14_DAY_FREE_TRIAL : translatedText.START_SUBSCRIPTION_NOW}
                    icon={shouldShowTrial ? 'ri-gift-line' : 'ri-arrow-right-line'}
                    className="trial-button"
                    onClick={shouldShowTrial ? onStartTrial : onStartSubscription}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                />
            </div>
            {!shouldShowTrial && !!actionErrorMessage && (
                <small className="p-error d-block mt-2">
                    {actionErrorMessage}{' '}
                    <button type="button" className="btn btn-link p-0 align-baseline" onClick={onAddPaymentMethodClick}>
                        {translatedText.ADD_PAYMENT_METHOD_LINK || ADD_PAYMENT_METHOD_LINK}
                    </button>
                </small>
            )}
        </div>
    );
};

export default PlanConfirmationModal;
